import type { DeliveryEstimateResponse } from "@interfaces/heap-api";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { ClientFetchService } from "@lib/helpers/clientFetchService";
import { type ApiError, useApi } from "@lib/helpers/useApi";
import type { Query } from "@tanstack/react-query";

type UseDeliveryEstimateParams = {
    skus: string[];
    country: string;
    enabled?: boolean;
};

const fetchDeliveryEstimate = async (skus: string[], country: string) => {
    const query = new URLSearchParams({ skus: skus.join(","), country });
    return await ClientFetchService.get<DeliveryEstimateResponse[]>(
        `/api/product/delivery-estimate?${query.toString()}`
    );
};

// Trusts the prefetched/cached estimate until the soonest `expiresAt` across all skus,
// so an SSR-baked page only refetches once its delivery dates are actually stale.
const getDeliveryEstimateStaleTime = (
    query: Query<DeliveryEstimateResponse[], ApiError, DeliveryEstimateResponse[]>
) => {
    const expiryTimes = (query.state.data ?? [])
        .map((estimate) => estimate.expiresAt)
        .filter((expiresAt): expiresAt is string => Boolean(expiresAt))
        .map((expiresAt) => new Date(expiresAt).getTime());

    if (expiryTimes.length === 0) return 0;

    return Math.max(0, Math.min(...expiryTimes) - Date.now());
};

export const useDeliveryEstimate = ({
    skus,
    country,
    enabled = true,
}: UseDeliveryEstimateParams) => {
    return useApi.useAsync<DeliveryEstimateResponse[]>(
        [QueryKeys.getDeliveryEstimate, skus, country],
        () => fetchDeliveryEstimate(skus, country),
        {
            enabled: enabled && skus.length > 0 && !!country,
            staleTime: getDeliveryEstimateStaleTime,
            // The estimate is a non-critical enhancement to USP copy; a failing request is unlikely to be transient,
            // so avoid hammering the endpoint with the default 3 retries.
            retry: 1,
        }
    );
};
